import React, { useState } from 'react';
import { Download, Upload } from 'lucide-react';
import { downloadSwapUfSample, importSwapUfFile, swapUfErrorMessage } from '../../api/swapUf';
import type { SwapUfImportResult } from '../../types/SwapUf';
import { t } from '../../i18n/t';
import { LopButton } from '../../components/LopButton';
import { LopFilePicker } from '../../components/LopFilePicker';
import { SwapUfModal } from './SwapUfModal';

interface Props {
  onClose: () => void;
  onImported: () => void;
}

export function SwapUfImportModal({ onClose, onImported }: Props) {
  const [file, setFile] = useState<File | null>(null);
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState<SwapUfImportResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const pick = (f: File | null) => {
    setFile(f);
    setResult(null);
    setError(null);
  };

  const run = async () => {
    if (!file) return;
    setRunning(true);
    setError(null);
    try {
      const r = await importSwapUfFile(file);
      setResult(r);
      onImported();
    } catch (e) {
      setError(swapUfErrorMessage(e));
    } finally {
      setRunning(false);
    }
  };

  const sample = async () => {
    try {
      await downloadSwapUfSample();
    } catch {
      setError(t('swapUf.import.sampleError'));
    }
  };

  return (
    <SwapUfModal
      title={t('swapUf.import.title')}
      onClose={onClose}
      wide
      footer={
        <>
          <LopButton
            id="swap-uf-import-run"
            variant="primary"
            icon={<Upload size={14} />}
            disabled={!file}
            loading={running}
            onClick={run}
          >
            {t('swapUf.import.run')}
          </LopButton>
          <LopButton variant="secondary" disabled={running} onClick={onClose}>
            {t('swapUf.modal.close')}
          </LopButton>
        </>
      }
    >
      <p className="swap-uf-info">{t('swapUf.import.explanation')}</p>
      <div className="swap-uf-upload">
        <LopFilePicker
          id="swap-uf-import-file"
          accept=".xlsx,.zip"
          files={file ? [file] : []}
          onChange={(files) => pick(files[0] ?? null)}
          title={t('swapUf.import.chooseFile')}
          hint={t('swapUf.import.dropHint')}
          replaceHint={t('swapUf.import.replaceFile')}
        />
        <LopButton id="swap-uf-sample-link" variant="link" icon={<Download size={14} />} onClick={sample}>
          {t('swapUf.import.sample')}
        </LopButton>
      </div>
      {error && (
        <div className="swap-uf-error" role="alert">
          {error}
        </div>
      )}
      {result && (
        <>
          <div id="swap-uf-import-summary" className="swap-uf-stats" aria-label={t('swapUf.import.summary', {
            created: result.created,
            exceptions: result.exceptions,
            duplicates: result.duplicates,
            total: result.totalRows,
          })}>
            <div className="swap-uf-stat">
              <span className="swap-uf-stat__value">{result.totalRows}</span>
              <span className="swap-uf-stat__label">{t('swapUf.import.statTotal')}</span>
            </div>
            <div className="swap-uf-stat swap-uf-stat--created">
              <span className="swap-uf-stat__value">{result.created}</span>
              <span className="swap-uf-stat__label">{t('swapUf.import.statCreated')}</span>
            </div>
            <div className="swap-uf-stat swap-uf-stat--exception">
              <span className="swap-uf-stat__value">{result.exceptions}</span>
              <span className="swap-uf-stat__label">{t('swapUf.import.statExceptions')}</span>
            </div>
            <div className="swap-uf-stat swap-uf-stat--duplicate">
              <span className="swap-uf-stat__value">{result.duplicates}</span>
              <span className="swap-uf-stat__label">{t('swapUf.import.statDuplicates')}</span>
            </div>
          </div>
          <div className="swap-uf-table-wrap">
            <table className="swap-uf-table">
              <thead>
                <tr>
                  <th>{t('swapUf.import.colRowNo')}</th>
                  <th>{t('swapUf.import.colFundNumber')}</th>
                  <th>{t('swapUf.import.colFundName')}</th>
                  <th>{t('swapUf.import.colTicker')}</th>
                  <th>{t('swapUf.import.colSide')}</th>
                  <th>{t('swapUf.import.colQty')}</th>
                  <th>{t('swapUf.import.colAcc')}</th>
                  <th>{t('swapUf.import.colAction')}</th>
                  <th>{t('swapUf.import.colStatus')}</th>
                  <th>{t('swapUf.import.colReason')}</th>
                </tr>
              </thead>
              <tbody>
                {result.rows.map((r) => (
                  <tr key={r.importRowId}>
                    <td className="swap-uf-nowrap">{r.rowNo}</td>
                    <td className="swap-uf-nowrap">{r.fundNumber}</td>
                    <td>{r.fundName}</td>
                    <td className="swap-uf-nowrap">{r.ticker}</td>
                    <td className="swap-uf-nowrap">{r.side === 'Buy' || r.side === 'Sell' ? t(`swapUf.side.${r.side}`) : r.side}</td>
                    <td className="swap-uf-nowrap">{r.qty === null ? '' : r.qty.toLocaleString('he-IL')}</td>
                    <td className="swap-uf-nowrap">{r.acc}</td>
                    <td className="swap-uf-nowrap">{r.action ? t(`swapUf.action.${r.action as 'new' | 'increase' | 'decrease'}`) : ''}</td>
                    <td className="swap-uf-nowrap">
                      <span className={`swap-uf-chip swap-uf-chip--${r.status}`}>
                        {t(`swapUf.status.${r.status}`)}
                      </span>
                    </td>
                    <td>{r.exceptionReason}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </SwapUfModal>
  );
}
